import type { ApiResponse } from './types';

export class ApiError extends Error {
  status: number;
  body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface HttpClientOptions {
  baseUrl: string;
  headers?: Record<string, string>;
}

export type RequestOptions = Omit<RequestInit, 'body' | 'headers'> & {
  headers?: Record<string, string>;
  body?: unknown;
};

export interface HttpClient {
  get<T>(path: string, init?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, init?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, init?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, init?: RequestOptions): Promise<T>;
  delete<T>(path: string, init?: RequestOptions): Promise<T>;
}

function join(base: string, path: string): string {
  if (!base.endsWith('/') && !path.startsWith('/')) return `${base}/${path}`;
  if (base.endsWith('/') && path.startsWith('/'))
    return `${base}${path.slice(1)}`;
  return `${base}${path}`;
}

export function createHttpClient({
  baseUrl,
  headers = {},
}: HttpClientOptions): HttpClient {
  const baseHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  function toBodyInit(body: unknown): BodyInit | null | undefined {
    if (body == null) return undefined;
    if (typeof body === 'string') return body;
    if (
      body instanceof Blob ||
      body instanceof FormData ||
      body instanceof URLSearchParams ||
      body instanceof ArrayBuffer
    ) {
      return body as BodyInit;
    }
    return JSON.stringify(body);
  }

  async function request<T>(
    method: string,
    path: string,
    init?: RequestOptions
  ): Promise<T> {
    const url = join(baseUrl, path);
    const { body: rawBody, headers: h, ...rest } = init ?? {};
    const headersMerged = { ...baseHeaders, ...(h ?? {}) };
    const bodyInit = toBodyInit(rawBody);
    if (bodyInit instanceof FormData) {
      delete headersMerged['Content-Type'];
    }
    const res = await fetch(url, {
      credentials: 'include',
      method,
      headers: headersMerged,
      ...rest,
      body: bodyInit,
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const text = await res.text();
    const payload = text.length
      ? isJson
        ? (() => {
            try {
              return JSON.parse(text) as unknown;
            } catch {
              return text as unknown;
            }
          })()
        : (text as unknown)
      : undefined;

    if (!res.ok) {
      const errObj =
        (isJson && payload && typeof payload === 'object'
          ? (payload as Partial<ApiResponse>).message ||
            (payload as Partial<ApiResponse>).error
          : undefined) || res.statusText;
      throw new ApiError(String(errObj), res.status, payload);
    }

    if (
      isJson &&
      payload &&
      typeof payload === 'object' &&
      'success' in (payload as Record<string, unknown>)
    ) {
      const obj = payload as ApiResponse<T>;
      return obj.data as T;
    }

    return payload as T;
  }

  return {
    get: <T>(path: string, init?: RequestOptions) =>
      request<T>('GET', path, init),
    post: <T>(path: string, body?: unknown, init?: RequestOptions) =>
      request<T>('POST', path, { ...(init ?? {}), body }),
    put: <T>(path: string, body?: unknown, init?: RequestOptions) =>
      request<T>('PUT', path, { ...(init ?? {}), body }),
    patch: <T>(path: string, body?: unknown, init?: RequestOptions) =>
      request<T>('PATCH', path, { ...(init ?? {}), body }),
    delete: <T>(path: string, init?: RequestOptions) =>
      request<T>('DELETE', path, init),
  };
}
